import { useState, useEffect } from "react";
import {
  Shield, CheckCircle, XCircle, FileText, Layers,
  User, UserCheck, FolderOpen, AlertTriangle, Loader2,
  ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import Header from "@/components/Header";
import EvidenceBoundary from "@/components/EvidenceBoundary";
import ReceiptDisplay from "@/components/ReceiptDisplay";
import { getKatlasStatus, getDossier, approveReview, refuseReview } from "@/lib/api";

export default function HomePage() {
  const [katlasStatus, setKatlasStatus] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    getKatlasStatus().then(setKatlasStatus).catch(console.error);
    getDossier().then(setDossier).catch(console.error);
  }, []);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const fn = action === "approve" ? approveReview : refuseReview;
      const result = await fn();
      setReceipt(result);
      setStep(4);
      toast.success(`Review ${action === "approve" ? "approved" : "refused"}`);
    } catch (err) {
      toast.error("Review action failed");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { n: 1, label: "Prepare dossier" },
    { n: 2, label: "Review evidence boundary" },
    { n: 3, label: "Sign review receipt" },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <Header katlasStatus={katlasStatus} />

      <main className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
        {/* Hero */}
        <section data-testid="hero-section" className="space-y-4 pt-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-50">
            TeardownCierge
          </h1>
          <p className="text-base text-zinc-400 leading-relaxed max-w-3xl">
            Governed technical review &middot; evidence boundaries &middot; signed assessment receipts
          </p>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-3xl">
            TeardownCierge helps researchers and reviewers turn a technical dossier,
            system teardown or security assessment into a governed review: what evidence
            was considered, who had authority to assess it, what was shared or withheld,
            and what receipt proves the review outcome.
          </p>
          <div className="border border-blue-500/20 bg-blue-500/5 px-4 py-3 max-w-3xl">
            <p className="text-sm text-blue-300 font-medium">
              Researcher prepares. Reviewer decides. KATLAS signs the receipt.
            </p>
          </div>
        </section>

        <Separator className="bg-zinc-800" />

        {/* Explainer */}
        <section data-testid="explainer-panel" className="space-y-3">
          <h2 className="text-base font-medium text-zinc-200">What this demo shows</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ExplainerCard
              title="For researchers"
              icon={User}
              text="TeardownCierge helps package a technical dossier so that reviewers can see the relevant evidence without exposing every raw artefact."
            />
            <ExplainerCard
              title="For reviewers"
              icon={UserCheck}
              text="TeardownCierge records the authority gate: who reviewed the dossier, what evidence was considered, what was withheld, and what outcome was approved or escalated."
            />
            <ExplainerCard
              title="For assessors"
              icon={Shield}
              text="The KATLAS receipt proves the governed review happened without turning the node into a data store."
            />
          </div>
        </section>

        <Separator className="bg-zinc-800" />

        {/* Journey strip */}
        <section data-testid="journey-strip" className="flex items-center gap-2 overflow-x-auto">
          {steps.map((s) => (
            <div key={s.n} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-2 px-4 py-2 border text-xs font-mono ${
                  step >= s.n
                    ? step === 4 && s.n <= 3
                      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
                      : s.n === step
                      ? "border-blue-500/30 bg-blue-500/10 text-blue-400"
                      : "border-zinc-700 bg-zinc-900 text-zinc-300"
                    : "border-zinc-800 bg-transparent text-zinc-600"
                }`}
              >
                <span className="font-semibold">{s.n}</span>
                <span className="uppercase tracking-widest text-[10px]">{s.label}</span>
              </div>
              {s.n < 3 && <ChevronRight className="h-3.5 w-3.5 text-zinc-700 flex-shrink-0" />}
            </div>
          ))}
        </section>

        {/* Step 1: Dossier Intake */}
        <section data-testid="dossier-intake" className="border border-zinc-800 bg-zinc-900 p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <FolderOpen className="h-4 w-4 text-blue-400" strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Step 1 &mdash; Dossier Intake
            </span>
          </div>

          {dossier ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoBlock label="System" value={dossier.system_name} sub={dossier.system_description} />
              <InfoBlock label="Review Purpose" value={dossier.review_purpose} />
              <InfoBlock label="Researcher" value={dossier.researcher?.role} sub={dossier.researcher?.description} />
              <InfoBlock label="Reviewer" value={dossier.reviewer?.role} sub={dossier.reviewer?.description} />
              <div className="md:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-600 block mb-1.5">Evidence Categories</span>
                <div className="flex flex-wrap gap-1.5">
                  {dossier.evidence_categories?.map((cat, i) => (
                    <Badge key={i} className="rounded-none text-[9px] font-mono bg-zinc-800 text-zinc-400 border-zinc-700">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-widest text-amber-400">Material withheld</span>
                </div>
                <p className="text-xs text-amber-400/70">
                  {dossier.withheld_evidence?.length || 0} categories of evidence are withheld
                  from the review summary (raw exploit details, private credentials,
                  sensitive configuration, internal notes, prompt context, proprietary source material).
                </p>
              </div>
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">Loading dossier...</p>
          )}

          {step === 1 && (
            <Button
              data-testid="continue-to-evidence"
              onClick={() => setStep(2)}
              className="rounded-none bg-blue-600 hover:bg-blue-500 text-white text-xs h-8 gap-1.5 mt-2"
            >
              Continue to Evidence Boundary
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </section>

        {/* Step 2: Evidence Boundary */}
        {step >= 2 && dossier && (
          <section data-testid="evidence-boundary-section" className="space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-blue-400" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Step 2 &mdash; Evidence Boundary
              </span>
            </div>
            <EvidenceBoundary shared={dossier.shared_evidence} withheld={dossier.withheld_evidence} />
            {step === 2 && (
              <Button
                data-testid="continue-to-gate"
                onClick={() => setStep(3)}
                className="rounded-none bg-blue-600 hover:bg-blue-500 text-white text-xs h-8 gap-1.5"
              >
                Continue to Reviewer Gate
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </section>
        )}

        {/* Step 3: Reviewer Authority Gate */}
        {step >= 3 && !receipt && (
          <section data-testid="reviewer-gate" className="border border-zinc-800 bg-zinc-900 p-5 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-4 w-4 text-blue-400" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Step 3 &mdash; Reviewer Authority Gate
              </span>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-3">
              <span className="text-[10px] uppercase tracking-widest text-zinc-600 block mb-0.5">Acting as</span>
              <span className="text-sm text-zinc-100 font-medium">Technical Reviewer / Assurance Lead</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                data-testid="approve-review-btn"
                onClick={() => handleAction("approve")}
                disabled={loading}
                className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-9 gap-1.5 px-5"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" strokeWidth={1.5} />}
                Approve governed review
              </Button>
              <Button
                data-testid="refuse-review-btn"
                onClick={() => handleAction("refuse")}
                disabled={loading}
                variant="outline"
                className="rounded-none border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs h-9 gap-1.5 px-5"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" strokeWidth={1.5} />}
                Refuse / escalate for further review
              </Button>
            </div>
          </section>
        )}

        {/* Step 4: Receipt */}
        {receipt && (
          <section data-testid="receipt-section" className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-400" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Step 4 &mdash; KATLAS-Signed Receipt
              </span>
            </div>
            <ReceiptDisplay receipt={receipt} />
          </section>
        )}

        <Separator className="bg-zinc-800" />

        {/* CAR footer */}
        <footer data-testid="car-footer" className="space-y-3 pb-8">
          <p className="text-sm text-zinc-400 leading-relaxed">
            <span className="text-blue-400 font-medium">Custody</span> controls the evidence.{" "}
            <span className="text-amber-400 font-medium">Authority</span> governs the review.{" "}
            <span className="text-emerald-400 font-medium">Receipts</span> prove what happened.
          </p>
          <p className="text-xs text-zinc-600 leading-relaxed">
            KATLAS records the governed action &mdash; not a copy of everyone&rsquo;s private data. Evidence stays at source.
          </p>
        </footer>
      </main>
    </div>
  );
}

function ExplainerCard({ title, icon: Icon, text }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
        <span className="text-xs font-medium text-zinc-200">{title}</span>
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed">{text}</p>
    </div>
  );
}

function InfoBlock({ label, value, sub }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-3">
      <span className="text-[10px] uppercase tracking-widest text-zinc-600 block mb-0.5">{label}</span>
      <span className="text-sm text-zinc-100">{value}</span>
      {sub && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{sub}</p>}
    </div>
  );
}
